import { monthLabel } from './dateHelpers';

const toRows = (counts = {}) => Object.entries(counts)
  .map(([label, count]) => ({ label, count }))
  .sort((a, b) => b.count - a.count);

const round = (value) => Math.round(value * 10) / 10;

const formatPercent = (count, total) => {
  if (!total) return '0%';
  return `${Math.round((count / total) * 100)}%`;
};

const buildTimeline = (monthlySummary) => {
  const totalDays = new Date(monthlySummary.year, monthlySummary.month, 0).getDate();
  const buckets = Array.from({ length: totalDays }, (_, index) => ({
    label: String(index + 1).padStart(2, '0'),
    total: 0,
    preventive: 0,
    serviceRequests: 0,
  }));

  const applyRecord = (record, type) => {
    const date = new Date(record.creationDate);
    if (Number.isNaN(date.getTime())) return;
    if (date.getUTCFullYear() !== monthlySummary.year || date.getUTCMonth() + 1 !== monthlySummary.month) return;
    const day = date.getUTCDate();
    const bucket = buckets[day - 1];
    if (!bucket) return;
    bucket.total += 1;
    bucket[type] += 1;
  };

  (monthlySummary.preventiveRecords || []).forEach((record) => applyRecord(record, 'preventive'));
  (monthlySummary.serviceRequestRecords || []).forEach((record) => applyRecord(record, 'serviceRequests'));

  return buckets.filter((bucket) => bucket.total > 0);
};

const buildResolutionStats = (serviceRequestRecords = []) => {
  const validDate = (value) => Boolean(value) && Number.isFinite(new Date(value).getTime());
  const resolved = serviceRequestRecords.filter((record) => (
    validDate(record.actualResolution) && validDate(record.creationDate)
    && new Date(record.actualResolution) >= new Date(record.creationDate)
  ));
  const withTarget = resolved.filter((record) => validDate(record.targetResolution));

  const avgResolutionHours = resolved.length
    ? round(resolved.reduce((sum, record) => {
      const createdAt = new Date(record.creationDate).getTime();
      const resolvedAt = new Date(record.actualResolution).getTime();
      return sum + ((resolvedAt - createdAt) / (1000 * 60 * 60));
    }, 0) / resolved.length)
    : null;

  const resolvedWithinTarget = withTarget.filter((record) => (
    new Date(record.actualResolution).getTime() <= new Date(record.targetResolution).getTime()
  )).length;

  return {
    avgResolutionHours,
    resolutionTrackedCount: resolved.length,
    resolvedWithinTarget,
    targetTrackedCount: withTarget.length,
    onTimeRate: withTarget.length ? round((resolvedWithinTarget / withTarget.length) * 100) : null,
  };
};

const buildServiceEvidence = (preventiveRecords, serviceRequestRecords) => {
  const inspectionCounts = { Pass: 0, Fail: 0, 'N/A': 0, Unrecorded: 0 };
  const checklist = new Map();
  const findings = [];
  const spareParts = [];
  const comments = [];
  let inspectionsRecorded = 0;

  preventiveRecords.forEach((record) => {
    const inspection = record.inspection || {};
    const items = inspection.items || [];
    if (items.some((item) => ['Pass', 'Fail', 'N/A'].includes(item.status))) inspectionsRecorded += 1;
    const reference = { scNumber: record.scNumber || '-', shopName: record.shopName || record.zone || '-' };
    items.forEach((item) => {
      const status = ['Pass', 'Fail', 'N/A'].includes(item.status) ? item.status : 'Unrecorded';
      inspectionCounts[status] += 1;
      const description = item.description?.trim() || 'Unspecified check';
      if (!checklist.has(description)) checklist.set(description, { description, Pass: 0, Fail: 0, 'N/A': 0, Unrecorded: 0 });
      checklist.get(description)[status] += 1;
      if (status === 'Fail') findings.push({ ...reference, description, remarks: item.remarks || 'No remarks recorded' });
    });
    (inspection.spareParts || []).forEach((part) => {
      if ([part.deviceName, part.description, part.quantity, part.remarks].some((value) => String(value || '').trim())) {
        spareParts.push({ ...reference, ...part });
      }
    });
    if (inspection.comments?.trim()) comments.push({ ...reference, comments: inspection.comments.trim() });
  });

  // Zone-wide PMs cannot establish which individual shops were visited.
  const records = [...preventiveRecords, ...serviceRequestRecords];
  const assetKeys = new Set(records.map((record) => record.assetNumber || record.assetTag).filter(Boolean));
  const zoneKeys = new Set(preventiveRecords.filter((record) => record.zone)
    .map((record) => `${record.location || ''}/${record.zone}`));

  return {
    inspectionCounts,
    inspectionsRecorded,
    checklistRows: [...checklist.values()],
    findings,
    spareParts,
    comments,
    assetCount: assetKeys.size,
    zoneCount: zoneKeys.size,
  };
};

export const buildMonthlySummaryInsights = (monthlySummary) => {
  if (!monthlySummary) return null;

  const totalClosed = monthlySummary.totals?.totalClosed || 0;
  const preventiveCount = monthlySummary.totals?.preventive || 0;
  const serviceRequestCount = monthlySummary.totals?.serviceRequests || 0;
  const locationRows = toRows(monthlySummary.byLocation);
  const priorityRows = toRows(monthlySummary.byPriority);
  const ppmRows = toRows(monthlySummary.preventiveByType);
  const srRows = (monthlySummary.serviceRequestDescriptionList || []).slice(0, 5);
  const timelineRows = buildTimeline(monthlySummary);
  const resolution = buildResolutionStats(monthlySummary.serviceRequestRecords || []);
  const busiestLocation = locationRows[0] || null;
  const dominantPriority = priorityRows[0] || null;
  const topPpmType = ppmRows[0] || null;
  const topIssue = srRows[0] || null;
  const preventiveShopRows = [...(monthlySummary.preventiveShopRecords || monthlySummary.preventiveRecords || [])]
    .reverse();
  const serviceRequestSummaryRows = [...(monthlySummary.serviceRequestSummaryRecords || monthlySummary.serviceRequestRecords || [])]
    .reverse();

  const evidence = buildServiceEvidence(preventiveShopRows, serviceRequestSummaryRows);
  const musicPlaylistSchedule = monthlySummary.musicPlaylistSchedule || 'Music playlists are updated every 5 weeks.';
  const scopeNote = 'Includes currently closed work orders created in the selected month (UTC). Closure dates may fall outside this month.';
  const serviceDeliveryRows = [
    { label: 'Music playlist refresh', value: 'Every 5 weeks', note: musicPlaylistSchedule },
    { label: 'Individual assets serviced', value: String(evidence.assetCount), note: 'Distinct asset numbers or tags on closed jobs; zone-wide PMs are counted separately.' },
    { label: 'Zones with preventive maintenance', value: String(evidence.zoneCount), note: 'Distinct location / zone combinations on closed preventive jobs.' },
    { label: 'Service requests resolved within target', value: resolution.onTimeRate === null ? 'Not available' : `${resolution.onTimeRate}%`, note: `${resolution.resolvedWithinTarget} of ${resolution.targetTrackedCount} requests with valid resolution and target dates.` },
  ];

  const narrative = [];
  narrative.push(
    `${monthLabel(monthlySummary.year, monthlySummary.month)} has ${totalClosed} currently closed QDF records created during the month, split between ${preventiveCount} preventive jobs (${formatPercent(preventiveCount, totalClosed)}) and ${serviceRequestCount} service requests (${formatPercent(serviceRequestCount, totalClosed)}).`
  );

  if (busiestLocation) {
    narrative.push(
      `${busiestLocation.label} carried the highest workload with ${busiestLocation.count} closed items (${formatPercent(busiestLocation.count, totalClosed)} of the month's activity), while ${dominantPriority ? `${dominantPriority.label} priority appeared most often at ${dominantPriority.count} records.` : 'priority demand remained evenly spread.'}`
    );
  }

  if (serviceRequestCount > 0 && resolution.onTimeRate !== null) {
    narrative.push(`${resolution.onTimeRate}% of service requests with valid resolution and target dates were resolved inside the target window.`);
  }

  if (topPpmType || topIssue) {
    narrative.push(
      `${topPpmType ? `${topPpmType.label} was the most common preventive program at ${topPpmType.count} closures.` : 'No preventive program trend was recorded.'} ${topIssue ? `The most repeated service request theme was "${topIssue.description}" with ${topIssue.count} occurrences.` : 'No repeated service request theme was recorded this month.'}`
    );
  }

  return {
    musicPlaylistSchedule,
    scopeNote,
    serviceDeliveryRows,
    evidence,
    totalClosed,
    preventiveCount,
    serviceRequestCount,
    locationRows,
    priorityRows,
    ppmRows,
    srRows,
    timelineRows,
    busiestLocation,
    dominantPriority,
    topPpmType,
    topIssue,
    resolution,
    narrative,
    preventiveShopRows,
    serviceRequestSummaryRows,
  };
};
